import type { Api } from "../api";
import { navigate } from "../nav";
import { empty, h, label, pill } from "../dom";
import { when } from "../format";
import { decisionWords, logRow, sentBy } from "../mail";
import { words } from "../orders";
import type { OutboundLogRow, Page } from "../types";
import { plain } from "../words";

// Mail: everything the outbound guard decided, newest first (outbound_log): sent, sent to the simulation recipients,
// drafted for review, blocked, failed. Who it was meant for and who actually got it, why, and who sent it; the email
// itself where there is one (each row carries its draftId). The filters live in the address
// (/mail?decision=BLOCKED&days=30), so a list can be linked to.

const DECISIONS: [string, string][] = [
  ["", "Everything"], ["SENT", "Sent"], ["REDIRECTED", "Sent to simulation recipients"], ["DRAFTED", "Drafted for review"],
  ["BLOCKED", "Blocked"], ["FAILED", "Failed"],
];
const DAYS = [7, 30, 90];

function daysAgo(n: number): string {
  return new Date(Date.now() - n * 86_400_000).toISOString().slice(0, 10);
}

const list = (a: string[]) => a.join(", ");

export async function mail(api: Api, query: URLSearchParams): Promise<HTMLElement> {
  const decision = DECISIONS.some(([d]) => d === query.get("decision")) ? query.get("decision")! : "";
  const days = DAYS.includes(Number(query.get("days"))) ? Number(query.get("days")) : 30;
  const cursor = /^[0-9]{1,6}$/.test(query.get("cursor") ?? "") ? query.get("cursor")! : undefined;
  const page = await api.tool<Page<Partial<OutboundLogRow>>>("outbound_log", {
    since: daysAgo(days), limit: 100, ...(decision ? { decision } : {}), ...(cursor ? { cursor } : {}),
  });

  const decisionSelect = h("select", { id: "decision" }, DECISIONS.map(([d, t]) => h("option", { value: d, selected: d === decision }, t)));
  const daysSelect = h("select", { id: "days" }, DAYS.map((n) => h("option", { value: n, selected: n === days }, `Last ${n} days`)));
  const go = (extra: Record<string, string> = {}) => {
    const q = new URLSearchParams({ ...(decisionSelect.value ? { decision: decisionSelect.value } : {}), days: daysSelect.value, ...extra });
    navigate(`/mail?${q}`);
  };
  decisionSelect.addEventListener("change", () => go());
  daysSelect.addEventListener("change", () => go());

  return h("div", { class: "page" },
    h("div", { class: "stack-tight" },
      h("h1", { class: "title" }, "Mail"),
      h("p", { class: "lede" }, "Every email the platform sent, drafted for review or stopped, newest first. " +
        (page.mode === "SIMULATION" ? "In simulation, nothing reaches anyone outside Tare: each went to the simulation recipients, marked [SIM]." : ""))),
    h("form", { class: "filters", role: "search", onsubmit: ((e: Event) => e.preventDefault()) as EventListener },
      h("div", { class: "field" }, h("label", { for: "decision" }, "What happened"), decisionSelect),
      h("div", { class: "field" }, h("label", { for: "days" }, "When"), daysSelect)),
    page.rows.length
      ? h("div", { class: "panel scroll" }, h("table", { class: "tbl wide" },
          h("thead", null, h("tr", null,
            h("th", { scope: "col" }, "When"), h("th", { scope: "col" }, "What happened"), h("th", { scope: "col", class: "col-text" }, "Email"),
            h("th", { scope: "col", class: "col-addr" }, "Meant for"), h("th", { scope: "col", class: "col-addr" }, "Delivered to"), h("th", { scope: "col" }, "By"))),
          h("tbody", null, page.rows.map(logRow).map((r) => {
            const d = decisionWords(r.decision);
            return h("tr", null,
              h("td", { class: "num nowrap" }, when(r.at)),
              h("td", null, pill(d.text, d.tone), r.reason && r.decision !== "REDIRECTED" ? h("div", { class: "caption" }, plain(r.reason)) : null),
              h("td", { class: "col-text" },
                r.draftId ? h("a", { href: `/draft/${r.draftId}` }, r.subject) : r.subject,
                r.vendorPo ? h("div", null, h("a", { class: "num caption", href: `/vendor-po/${r.vendorPo}` }, `Vendor PO ${r.vendorPo}`)) : null,
                h("div", { class: "caption" }, [words(r.purpose).toLowerCase(), r.attachments.length ? `attached: ${list(r.attachments)}` : null]
                  .filter(Boolean).join(" · "))),
              h("td", { class: "col-addr" }, list(r.intendedTo), r.intendedCc.length ? h("div", { class: "caption" }, `cc ${list(r.intendedCc)}`) : null),
              h("td", { class: "col-addr" }, r.deliveredTo.length ? list(r.deliveredTo) : h("span", { class: "muted" }, "nobody")),
              h("td", null, sentBy(r.source)));
          }))))
      : empty("Nothing here", "Nothing matches in this time; try a longer period or Everything."),
    page.next_cursor
      ? h("div", null, h("a", { class: "button", href: `/mail?${new URLSearchParams({ ...(decision ? { decision } : {}), days: String(days), cursor: page.next_cursor })}` }, "Next 100"))
      : null,
    label(page.mode === "SIMULATION" ? "Simulation" : "Live"));
}
